import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useMsal } from '@azure/msal-react'
import { getUrls, ShortenedUrl, ShortenResponse } from '../lib/api'
import ShortenForm from '../components/ShortenForm'
import ResultCard from '../components/ResultCard'
import UrlRow from '../components/UrlRow'
import styles from './Home.module.css'

function isExpiredUrl(url: ShortenedUrl): boolean {
  return !!url.expiresAt && new Date(url.expiresAt) < new Date()
}

export default function Home() {
  const { instance, accounts } = useMsal()
  const user = accounts[0]

  const [urls, setUrls] = useState<ShortenedUrl[]>([])
  const [lastResult, setLastResult] = useState<ShortenResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [expiredOpen, setExpiredOpen] = useState(false)
  const searchRef = useRef(search)
  const PAGE_SIZE = 20

  const activeUrls = useMemo(() => urls.filter(u => !isExpiredUrl(u)), [urls])
  const expiredUrls = useMemo(() => urls.filter(u => isExpiredUrl(u)), [urls])

  const load = useCallback(async (p = 1, q = '') => {
    setLoading(true)
    try {
      const data = await getUrls(p, PAGE_SIZE, q || undefined)
      setUrls(prev => p === 1 ? data : [...prev, ...data])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load(1) }, [load])

  // Debounce search: wait 300ms after typing stops before querying
  useEffect(() => {
    searchRef.current = search
    const timer = setTimeout(() => {
      if (searchRef.current === search) {
        setPage(1)
        load(1, search)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [search, load])

  function handleCreated(r: ShortenResponse) {
    setLastResult(r)
    setSearch('')
    load(1, '')
  }

  function handleDeleted(code: string) {
    setUrls(prev => prev.filter(u => u.shortCode !== code))
  }

  function handleRenewed(updated: ShortenedUrl) {
    setUrls(prev => prev.map(u => u.id === updated.id ? updated : u))
  }

  function loadMore() {
    const next = page + 1
    setPage(next)
    load(next, search)
  }

  function handleSignOut() {
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin })
  }

  const hasAnyUrls = urls.length > 0 || search

  return (
    <div className={styles.page}>
      <div className={styles.blob} />

      <div className={styles.userBar}>
        <span className={styles.userName}>{user?.name ?? user?.username}</span>
        <button className={styles.signOutBtn} onClick={handleSignOut}>Sign out</button>
      </div>

      <header className={styles.header}>
        <div className={styles.logoPill}>
          <img src="/brambles-logo.png" alt="Brambles" className={styles.logoImg} />
        </div>
        <p className={styles.tagline}>Internal link shortener for Brambles teams.</p>
      </header>

      <main className={styles.main}>
        <ShortenForm onCreated={handleCreated} />

        <AnimatePresence>
          {lastResult && (
            <motion.div key={lastResult.shortCode}>
              <ResultCard result={lastResult} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Active links ── */}
        {(activeUrls.length > 0 || search) && (
          <section className={styles.list}>
            <div className={styles.listHeader}>
              <h2 className={styles.listTitle}>Recent links</h2>
              <span className={styles.listCount}>{activeUrls.length} total</span>
            </div>
            <div className={styles.searchRow}>
              <input
                className={styles.searchInput}
                type="search"
                placeholder="Search by URL or alias…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {loading && search && <span className={styles.searchSpinner} />}
            </div>
            <AnimatePresence mode="popLayout">
              {activeUrls.map(u => (
                <UrlRow
                  key={u.id}
                  url={u}
                  isExpired={false}
                  onDeleted={handleDeleted}
                  onRenewed={handleRenewed}
                />
              ))}
            </AnimatePresence>
            {!loading && search && activeUrls.length === 0 && expiredUrls.length === 0 && (
              <p className={styles.searchEmpty}>No links match &ldquo;{search}&rdquo;</p>
            )}
            {!search && urls.length === page * PAGE_SIZE && (
              <button className={styles.loadMore} onClick={loadMore} disabled={loading}>
                {loading ? 'Loading…' : 'Load more'}
              </button>
            )}
          </section>
        )}

        {/* ── Expired links (collapsible) ── */}
        {expiredUrls.length > 0 && (
          <section className={styles.expiredSection}>
            <button
              className={styles.expiredHeader}
              onClick={() => setExpiredOpen(v => !v)}
              aria-expanded={expiredOpen}
            >
              <span className={styles.expiredChevron} data-open={expiredOpen}>›</span>
              <h2 className={styles.listTitle}>Expired links</h2>
              <span className={styles.expiredCount}>{expiredUrls.length}</span>
            </button>

            <AnimatePresence initial={false}>
              {expiredOpen && (
                <motion.div
                  key="expired-list"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: 'easeInOut' }}
                  className={styles.expiredList}
                >
                  <AnimatePresence mode="popLayout">
                    {expiredUrls.map(u => (
                      <UrlRow
                        key={u.id}
                        url={u}
                        isExpired={true}
                        onDeleted={handleDeleted}
                        onRenewed={handleRenewed}
                      />
                    ))}
                  </AnimatePresence>
                </motion.div>
              )}
            </AnimatePresence>
          </section>
        )}

        {!loading && !hasAnyUrls && (
          <motion.div
            className={styles.empty}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <p>No links yet. Paste a URL above to get started.</p>
          </motion.div>
        )}
      </main>

      <footer className={styles.footer}>
        <span className={styles.footerText}>
          © 2025 Brambles — Internal Tool
        </span>
      </footer>
    </div>
  )
}
